// src/auth/auth-store.ts
import { create } from "zustand";

// src/auth/cookie-utils.ts
var getCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2] ?? "") : null;
};
var setCookie = (name, value, days) => {
  if (typeof document === "undefined") return;
  let expires = "";
  if (days) {
    const date = /* @__PURE__ */ new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1e3);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + encodeURIComponent(value || "") + expires + "; path=/; SameSite=Strict";
};
var deleteCookie = (name) => {
  if (typeof document === "undefined") return;
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=Strict";
};

// src/auth/auth-store.ts
var useAuthStore = create((set) => ({
  user: null,
  accessToken: getCookie("fm_access_token"),
  hydrating: true,
  setAuth: (user, accessToken, refreshToken) => {
    setCookie("fm_access_token", accessToken, 7);
    if (refreshToken) {
      setCookie("fm_refresh_token", refreshToken, 30);
    }
    set({ user, accessToken, hydrating: false });
  },
  setUser: (user) => set({ user, hydrating: false }),
  setAccessToken: (accessToken) => {
    setCookie("fm_access_token", accessToken, 7);
    set({ accessToken });
  },
  clear: () => {
    deleteCookie("fm_access_token");
    deleteCookie("fm_refresh_token");
    set({ user: null, accessToken: null, hydrating: false });
  },
  setHydrated: () => set({ hydrating: false })
}));

// src/auth/api-types.ts
var ApiError = class extends Error {
  constructor(status, envelope) {
    super(envelope.error.message);
    this.status = status;
    this.envelope = envelope;
    this.name = "ApiError";
  }
  status;
  envelope;
  get code() {
    return this.envelope.error.code;
  }
};

// src/auth/api-client.ts
var API_BASE_URL = import.meta.env["VITE_API_BASE_URL"] ?? "http://localhost:3000";
var DEFAULT_TENANT = import.meta.env?.VITE_DEFAULT_TENANT ?? "yk-almoayyed";
var refreshInFlight = null;
async function callRefresh() {
  try {
    const refreshToken = getCookie("fm_refresh_token") || "";
    const res = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Platform": "mobile"
      },
      body: JSON.stringify({ refreshToken })
    });
    if (!res.ok) return null;
    const body = await res.json();
    useAuthStore.getState().setAccessToken(body.data.accessToken);
    if (body.data.refreshToken) {
      setCookie("fm_refresh_token", body.data.refreshToken, 30);
    }
    return body.data.accessToken;
  } catch {
    return null;
  }
}
function getRefreshPromise() {
  if (!refreshInFlight) {
    refreshInFlight = callRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
async function apiFetch(path, opts = {}) {
  return apiFetchInner(
    path,
    opts,
    /* retried= */
    false
  );
}
async function apiFetchInner(path, opts, retried) {
  const { accessToken } = useAuthStore.getState();
  const headers = new Headers(opts.headers);
  headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  const tenant = useAuthStore.getState().user?.orgSlug ?? DEFAULT_TENANT;
  headers.set("X-Tenant", tenant);
  const rawBody = opts.body !== void 0 ? JSON.stringify(opts.body) : null;
  const { body: _body, headers: _headers, ...restOpts } = opts;
  const init = {
    ...restOpts,
    headers,
    credentials: "include",
    ...rawBody !== null ? { body: rawBody } : {}
  };
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, init);
  } catch (err) {
    throw new ApiError(0, {
      error: {
        code: "network_unreachable",
        message: err instanceof Error ? err.message : "Network unreachable",
        requestId: "network-failure",
        retryable: true
      }
    });
  }
  if (res.status === 204) return void 0;
  if (res.status === 401 && !retried) {
    const body = await res.clone().json().catch(() => null);
    const code = body?.error.code;
    if (code === "unauthorized") {
      const fresh = await getRefreshPromise();
      if (fresh) return apiFetchInner(
        path,
        opts,
        /* retried */
        true
      );
      useAuthStore.getState().clear();
    }
    if (code === "refresh_token_invalid" || code === "refresh_token_reuse_detected") {
      useAuthStore.getState().clear();
    }
  }
  if (!res.ok) {
    const envelope = await parseErrorEnvelope(res);
    throw new ApiError(res.status, envelope);
  }
  try {
    return await res.json();
  } catch (err) {
    throw new ApiError(res.status, {
      error: {
        code: "invalid_response",
        message: err instanceof Error ? err.message : "Invalid response body",
        requestId: "parse-failure",
        retryable: false
      }
    });
  }
}
async function parseErrorEnvelope(res) {
  try {
    return await res.json();
  } catch {
    return {
      error: {
        code: "server_error",
        message: `Request failed with status ${res.status}`,
        requestId: "no-body",
        retryable: res.status >= 500
      }
    };
  }
}
function createAuthApi(config) {
  const withProduct = (body) => config.product ? { ...body, product: config.product } : body;
  return {
    signIn(body) {
      return apiFetch("/api/v1/auth/sign-in", {
        method: "POST",
        headers: { "X-Platform": "mobile" },
        body: withProduct(body)
      });
    },
    verifyOAuthCode(body) {
      return apiFetch("/api/v1/auth/oauth", {
        method: "POST",
        headers: { "X-Platform": "mobile" },
        body: withProduct({
          grant_type: "authorization_code",
          redirect_uri: import.meta.env?.VITE_AUTHORIZED_REDIRECT_URL,
          client_id: import.meta.env?.VITE_CLIENT_ID,
          ...body
        })
      });
    },
    signOut() {
      return apiFetch("/api/v1/auth/sign-out", {
        method: "POST",
        body: {}
      });
    },
    fetchMe() {
      return apiFetch("/api/v1/auth/me", { method: "GET" });
    },
    forgotPassword(body) {
      return apiFetch("/api/v1/auth/forgot-password", {
        method: "POST",
        body: withProduct(body)
      });
    },
    resetPassword(body) {
      return apiFetch("/api/v1/auth/reset-password", {
        method: "POST",
        body
      });
    }
  };
}

// src/auth/public-paths.ts
function createIsPublicPath(config) {
  const exact = new Set(config.paths);
  const prefixes = config.prefixes ?? [];
  return (pathname) => {
    if (exact.has(pathname)) return true;
    return prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  };
}

// src/auth/auth-gate.tsx
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Fragment, jsx } from "react/jsx-runtime";
function AuthGate({
  children,
  fetchMe,
  isPublicPath,
  signInPath = "/sign-in"
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hydrating, clear, setHydrated } = useAuthStore();
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      if (user) {
        setHydrated();
        return;
      }
      try {
        const res = await fetchMe();
        if (cancelled) return;
        useAuthStore.getState().setUser(res.data.user);
      } catch {
        if (cancelled) return;
        clear();
      }
    }
    void hydrate();
    return () => {
      cancelled = true;
    };
  }, []);
  useEffect(() => {
    if (hydrating) return;
    if (isPublicPath(location.pathname)) return;
    if (!user) {
      void navigate(signInPath);
    }
  }, [hydrating, user, location.pathname, navigate, isPublicPath, signInPath]);
  if (hydrating) {
    return /* @__PURE__ */ jsx("main", { className: "mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12 text-center", children: /* @__PURE__ */ jsx("p", { className: "text-sm text-muted", children: "Loading\u2026" }) });
  }
  if (!user && !isPublicPath(location.pathname)) return /* @__PURE__ */ jsx(Fragment, {});
  return /* @__PURE__ */ jsx(Fragment, { children });
}

// src/components/user-menu.tsx
import { useState } from "react";
import { useNavigate as useNavigate2 } from "react-router-dom";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@yk/ui";
import { ChevronDown, LogOut, User } from "@yk/ui/icons";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
function UserMenu({
  signOut,
  signInPath = "/sign-in",
  extraItems
}) {
  const navigate = useNavigate2();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [isSigningOut, setIsSigningOut] = useState(false);
  if (!user) return null;
  async function handleSignOut() {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      try {
        await signOut();
      } catch (err) {
        console.error("sign-out failed; clearing locally", err);
      }
      clear();
      await navigate(signInPath);
    } finally {
      setIsSigningOut(false);
    }
  }
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx2(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", className: "gap-2", children: [
      /* @__PURE__ */ jsx2(User, { className: "size-4", "aria-hidden": "true" }),
      /* @__PURE__ */ jsx2("span", { className: "hidden max-w-40 truncate sm:inline", children: user.email }),
      /* @__PURE__ */ jsx2(ChevronDown, { className: "size-4", "aria-hidden": "true" })
    ] }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "min-w-56", children: [
      /* @__PURE__ */ jsx2(
        DropdownMenuLabel,
        {
          className: "truncate font-normal text-muted",
          "data-testid": "user-display",
          children: user.email
        }
      ),
      /* @__PURE__ */ jsx2(DropdownMenuSeparator, {}),
      extraItems,
      /* @__PURE__ */ jsxs(
        DropdownMenuItem,
        {
          onSelect: (e) => {
            e.preventDefault();
            void handleSignOut();
          },
          disabled: isSigningOut,
          "data-testid": "sign-out-button",
          children: [
            /* @__PURE__ */ jsx2(LogOut, { className: "size-4", "aria-hidden": "true" }),
            "Sign out"
          ]
        }
      )
    ] })
  ] });
}

// src/auth/permissions.ts
var BYPASS_ROLES = ["super_admin"];
var ORG_BYPASS_ROLES = ["company_administrator"];
function scopeOf(code) {
  return code.slice(code.lastIndexOf(":") + 1);
}
function hasPermission(user, code) {
  if (!user) return false;
  if (user.roles?.some((r) => BYPASS_ROLES.includes(r))) return true;
  if (scopeOf(code) !== "platform" && user.roles?.some((r) => ORG_BYPASS_ROLES.includes(r))) {
    return true;
  }
  return user.permissions?.includes(code) ?? false;
}
function usePermissions() {
  const user = useAuthStore((s) => s.user);
  return { can: (code) => hasPermission(user, code) };
}

// src/components/forbidden-403.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
function Forbidden403({
  homePath = "/dashboard"
}) {
  return /* @__PURE__ */ jsxs2("div", { className: "flex min-h-[60vh] flex-col items-center justify-center gap-3 text-center", children: [
    /* @__PURE__ */ jsx3("p", { className: "text-4xl font-bold", children: "403" }),
    /* @__PURE__ */ jsx3("p", { className: "text-lg font-semibold", children: "You don't have access to this page" }),
    /* @__PURE__ */ jsx3("p", { className: "text-sm text-muted", children: "Your role doesn't include permission for this area." }),
    /* @__PURE__ */ jsx3(
      "a",
      {
        href: homePath,
        className: "mt-2 text-sm font-medium text-primary underline",
        children: "Back to dashboard"
      }
    )
  ] });
}

// src/auth/require-permission.tsx
import { Fragment as Fragment2, jsx as jsx4 } from "react/jsx-runtime";
function RequirePermission({
  code,
  children,
  fallback
}) {
  const { can } = usePermissions();
  if (!can(code)) return /* @__PURE__ */ jsx4(Fragment2, { children: fallback ?? /* @__PURE__ */ jsx4(Forbidden403, {}) });
  return /* @__PURE__ */ jsx4(Fragment2, { children });
}
export {
  ApiError,
  AuthGate,
  BYPASS_ROLES,
  Forbidden403,
  RequirePermission,
  UserMenu,
  apiFetch,
  createAuthApi,
  createIsPublicPath,
  hasPermission,
  useAuthStore,
  usePermissions
};
//# sourceMappingURL=index.js.map